import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Chip
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { grey, blueGrey } from '@mui/material/colors';
import {
  data as docStatus,
  DOCENC,
  DOCVAL,
  DOCREF
} from '@lba-dev/package.local-globals/docStatus';
import { dayjs } from '@lba-dev/package.local-globals/dayjs';
import { withStyles } from 'tss-react/mui';

import DatePicker from '../DatePicker';

const styles = () => ({
  metadataSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
    padding: '12px 0'
  },
  sectionTitle: {
    fontSize: '0.875rem',
    color: blueGrey[800],
    fontWeight: 600,
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  metadataLabel: {
    fontSize: '0.625rem',
    color: grey[600],
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4
  },
  metadataValue: {
    fontSize: '0.875rem',
    color: blueGrey[800],
    marginBottom: 12,
    fontWeight: 500
  },
  metadataValueEmpty: {
    fontSize: '0.875rem',
    color: grey[400],
    marginBottom: 12,
    fontStyle: 'italic'
  },
  metadataWithTooltip: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 12
  },
  infoIcon: {
    fontSize: 16,
    color: grey[500],
    cursor: 'help'
  },
  validationContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16
  },
  validationLabel: {
    fontSize: '0.75rem',
    color: grey[700],
    fontWeight: 600,
    textTransform: 'uppercase'
  },
  validationChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 28,
    borderRadius: 14
  },
});

// ============================================================================
// UTILITAIRES MÉTADONNÉES IA
// ============================================================================

/**
 * Extrait un élément de métadonnées par type et champ
 */
const getMetadataItem = (metadata, type, champ) => {
  if (!Array.isArray(metadata)) {
    return null;
  }
  return metadata.find(item => item.type === type && item.champ === champ);
};

/**
 * Récupère le badge de validation (recommandation + confidence)
 */
const getValidationBadge = (metadata) => {
  const recommendation = getMetadataItem(
    metadata,
    'recommendation',
    'recommendation'
  );
  const confidence = getMetadataItem(metadata, 'confidence', 'confidence');

  if (!recommendation?.value) {
    return null;
  }

  const recommendationMap = {
    'APPROVE': DOCVAL,
    'REVIEW_REQUIRED': DOCENC,
    'REJECT': DOCREF
  };

  const statusId = recommendationMap[recommendation.value];
  const status = docStatus.find(s => s._id === statusId);

  return status ? { status, confidence } : null;
};

/**
 * Récupère tous les champs de type 'data' || 'check'
 */
const getFields = (metadata, key = 'data') =>
  metadata.filter(item => item.type === key);

/**
 * Formatte la valeur selon le champ
 */
const formatFieldValue = (champ, value) => {
  if (!value) {
    return null;
  }

  // Dates
  if (['endDate', 'startDate'].includes(champ)) {
    return dayjs(value).format('DD/MM/YYYY');
  }

  return value;
};

// ============================================================================
// COMPOSANTS
// ============================================================================

/**
 * Composant pour afficher un champ de métadonnées avec tooltip
 */
const MetadataField = ({ label, value, message, formatter, classes }) => {
  const hasValue = value !== null && value !== undefined && value !== '';
  const displayValue = (hasValue && (formatter ? formatter(value) :
    value)) || 'Non récupéré';

  return (
    <Box>
      <Typography className={classes.metadataLabel}>
        {label}
      </Typography>
      <Box className={classes.metadataWithTooltip}>
        <Typography
          className={
            hasValue ? classes.metadataValue : classes.metadataValueEmpty
          }
        >
          {displayValue}
        </Typography>
        {message && hasValue && (
          <Tooltip title={message} arrow>
            <InfoOutlined className={classes.infoIcon} />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

/**
 * Composant pour afficher et éditer la date d'expiration
 */
const SimpleDateField = ({
  label,
  value,
  message,
  classes,
  status,
  onDateChange,
  isManuallyModified
}) => {
  const canEdit = status === DOCENC;
  const displayValue = value
    ? dayjs(value).format('DD/MM/YYYY')
    : 'Non récupéré';

  const handleDateChange = (date) => {
    if (date && date.isValid()) {
      onDateChange(date.format('YYYY-MM-DD'));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography className={classes.metadataLabel}>{label}</Typography>
        {isManuallyModified && (
          <Chip
            label="Modifiée manuellement"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        )}
      </Box>
      {canEdit ? (
        <Box>
          <DatePicker
            startDate={value ? new Date(value) : ''}
            onChange={handleDateChange}
            label=""
            beforeDate={new Date()}
            withInput
          />
          {message && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                mt: 0.5
              }}
            >
              {message}
            </Typography>
          )}
        </Box>
      ) : (
        <Box className={classes.metadataWithTooltip}>
          <Typography className={classes.metadataValue}>
            {displayValue}
          </Typography>
          {message && (
            <Tooltip title={message} arrow>
              <InfoOutlined className={classes.infoIcon} />
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Composant pour le badge de validation
 */
const ValidationBadge = ({ status, confidence, classes }) => {
  if (!status) {
    return null;
  }

  return (
    <Box className={classes.validationContainer}>
      <Typography className={classes.validationLabel}>
        Note IA :
      </Typography>
      <Chip
        label={`${status.name}${confidence?.value
          ? ` (${confidence.value}%)` : ''}`}
        className={classes.validationChip}
        style={{
          backgroundColor: status.color[500],
          color: 'white'
        }}
        size="small"
      />
    </Box>
  );
};

/**
 * Composant principal pour afficher les métadonnées d'assurance
 */
const InsuranceMetadataDisplay = ({
  metadata,
  classes,
  status,
  onEndDateChange,
  currentEndDate,
  isDateManuallyModified
}) => {
  if (!metadata || !Array.isArray(metadata) || metadata.length === 0) {
    return null;
  }

  const validationBadge = getValidationBadge(metadata);
  const dataFields = getFields(metadata, 'data');

  return (
    <Box>
      <Typography className={classes.sectionTitle}>
        Analyse IA
      </Typography>

      <ValidationBadge {...validationBadge} classes={classes} />

      {/* Affichage dynamique des champs data */}
      {dataFields.length > 0 && (
        <Box className={classes.metadataSection}>
          {dataFields.map((field) => {
            // Champ endDate éditable avec un simple input
            if (field.champ === 'endDate' && onEndDateChange) {
              return (
                <SimpleDateField
                  key={field.champ}
                  label={field.label}
                  value={currentEndDate || field.value}
                  message={field.message}
                  classes={classes}
                  status={status}
                  onDateChange={onEndDateChange}
                  isManuallyModified={isDateManuallyModified}
                />
              );
            }
            // Autres champs en lecture seule
            return (
              <MetadataField
                key={field.champ}
                label={field.label}
                value={field.value}
                message={field.message}
                formatter={(val) => formatFieldValue(field.champ, val)}
                classes={classes}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default withStyles(InsuranceMetadataDisplay, styles);
